import { Box, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { JobSelector } from "../../../../../../components/UIToolkit/molecules/JobSelector";
import SystemDialog from "../../../../../../components/UIToolkit/Popups/Dialog";
import type { Job } from "../../../../../../utils/models";

export interface AdditionalJobsDialogProps {
	open: boolean;
	roleString: string;
	/** Department-enabled jobs; the job the role string syncs as is filtered out — an additional job can never repeat it. */
	jobs: Job[];
	/** jobs[0] on the mapping — the job employees with this role string sync as. */
	syncJobId: string;
	/** The row's current additional job ids (jobs[1:]) — pre-selected when the dialog opens. */
	initialSelectedIds: string[];
	/** Linked raw-HRIS employees under this role string; shown so the admin knows the blast radius. */
	linkedEmployeeCount: number;
	onConfirm: (additionalJobIds: string[]) => void;
	onClose: () => void;
}

/**
 * Modal for editing the additional jobs a role string maps to (DAT-466). Saving applies the
 * selection to all employees currently linked to the role string and to every new employee created
 * with it — the mapping is the source of truth, so deselected jobs are also removed from those employees.
 */
export const AdditionalJobsDialog: FC<AdditionalJobsDialogProps> = ({
	open,
	roleString,
	jobs,
	syncJobId,
	initialSelectedIds,
	linkedEmployeeCount,
	onConfirm,
	onClose,
}) => {
	const { t } = useTranslation();
	const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);

	// Re-seed the selection each time the dialog opens so a cancelled edit doesn't leak into the next one.
	useEffect(() => {
		if (open) setSelectedIds(initialSelectedIds);
	}, [open, initialSelectedIds]);

	const selectableJobs = useMemo(() => jobs.filter((j) => j.id !== syncJobId), [jobs, syncJobId]);
	const hasChanges = useMemo(
		() => selectedIds.length !== initialSelectedIds.length || selectedIds.some((id) => !initialSelectedIds.includes(id)),
		[selectedIds, initialSelectedIds],
	);

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("hris.mapping.additionalJobs.dialog.title", { roleString })}
			dataTestId="hris-mapping-additional-jobs-dialog"
			confirmDisabled={!hasChanges}
			onConfirm={() => onConfirm(selectedIds)}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1, minWidth: 360 }}>
					<Typography variant="body2" color="text.secondary">
						{t("hris.mapping.additionalJobs.dialog.hint", { count: linkedEmployeeCount })}
					</Typography>
					<JobSelector jobs={selectableJobs} selectedJobIds={selectedIds} onSelectedJobsChanged={setSelectedIds} hideAllOption />
				</Box>
			}
		/>
	);
};
